<template>
  <v-app :class="[`${languageClass}`]">
    <v-app-bar v-if="!fromTngMiniProgram" class="main-header" color="white" height="64" style="z-index: 8">
      <v-container class="ma-auto py-0 body-container">
        <v-layout class="align-center pa-0">
          <nuxt-link
            v-if="!foreignDomainFromMichelin"
            class="index-link"
            :class="{ textBlock: isNoLogoImage, funbookV2LogoBlock: isFunBookV2 && !isNoLogoImage }"
            :to="homeLink"
          >
            <span v-if="isNoLogoImage" class="branchName">{{ branchName }}</span>
            <img v-else-if="isFunBookV2" :src="headerLogoImage" class="wd-h-11 wd-max-w-33" />
            <FunnowIcon v-else class="wd-max-h-16 wd-max-w-33" raw :image-path="headerLogoImage" />
          </nuxt-link>
          <a v-else class="index-link" href="#">
            <v-img style="max-height: 64px" cover :src="headerLogoImage" />
          </a>
        </v-layout>
      </v-container>
    </v-app-bar>

    <v-main>
      <v-container class="main__container" fluid>
        <slot />
      </v-container>
    </v-main>

    <root-message-dialog />
    <!-- <Auth /> -->
  </v-app>
</template>

<script>
import { mapGetters } from 'vuex'

import tools from '@/mixin/tools'
import utils from '@/utils'
import { faviconUrl } from '@/view-models/common'

import RootMessageDialog from '@/components/dialog/root-message-dialog.vue'

import { useBookingEngine } from '@/composition/use-booking-engine'
import { useFunBookV2 } from '@/composition/useFunbookV2'
import { useIdentifyAndAttribution } from '@/composition/useGtm'
import { usePlatformStore } from '@/store-pinia/platform'
import { useFunBookV2Store } from '@/store-pinia/funbookV2'

export default defineNuxtComponent({
  components: {
    RootMessageDialog,
    FunnowIcon: defineAsyncComponent(() => import('@/components/icons/funnow-icon.vue')),
  },
  mixins: [tools],
  setup() {
    const config = useRuntimeConfig()
    const nuxtApp = useNuxtApp()
    const { $store: store } = nuxtApp

    useForeignDomain()

    const i18n = useI18n()
    const platformStore = usePlatformStore()
    const route = useRoute()
    const { isFunBookV2 } = storeToRefs(platformStore)
    const { logoImage: logoFromFunBookV2, branchName } = storeToRefs(useFunBookV2Store())
    const { headerLogoImage: logoFromBookingEngine } = useBookingEngine()
    const { branchId } = useFunBookV2()
    const localePath = useLocalePath()
    useIdentifyAndAttribution(nuxtApp)

    const product = computed(() => store.getters['product/productInfo'])
    const headerLogoImage = computed(() => (isFunBookV2.value ? logoFromFunBookV2.value : logoFromBookingEngine.value))
    const isNoLogoImage = computed(() => isFunBookV2.value && !headerLogoImage.value)

    const pageTitle = computed(() =>
      isFunBookV2.value
        ? `${product.value?.product_name || ''} - ${branchName.value} | ${i18n.t('default_link_title_funbook')}`
        : i18n.t('funnow_title')
    )
    const description = computed(() => (isFunBookV2.value ? utils.removeNewLine(product.value.description || '') : i18n.t('brand_description')))
    const ogImage = computed(() =>
      isFunBookV2.value ? utils.CDN + utils.imagePath(product.value.cover_image || '') : 'https://cdn.myfunnow.com/web/og_images/web_cover.png'
    )
    const currUrl = computed(() => `${config.public.BASE_URL}${route.path}`)

    useHead(() => ({
      htmlAttrs: {
        lang: i18n.locale.value,
      },
      title: pageTitle.value,
      meta: [
        { hid: 'title', name: 'title', content: pageTitle.value },
        { hid: 'description', name: 'description', content: description.value },
        { hid: 'og:title', property: 'og:title', content: pageTitle.value },
        { hid: 'og:description', property: 'og:description', content: description.value },
        { hid: 'og:image', property: 'og:image', content: ogImage.value },
        { property: 'fb:admins', content: 836889489 },
        { property: 'fb:app_id', content: config.public.FB_APP_ID },
        { property: 'og:url', content: currUrl.value },
        { property: 'al:web:url', content: currUrl.value },
        { name: 'robots', content: 'noindex' },
      ],
      link: [
        { rel: 'icon', type: 'image/x-icon', href: faviconUrl },
        { rel: 'canonical', href: currUrl.value },
      ],
    }))

    return {
      isFunBookV2,
      isNoLogoImage,
      headerLogoImage,
      product,
      branchName,
      branchId,
      localePath,
    }
  },
  computed: {
    languageClass() {
      return `language-${this.$i18n.locale}`
    },
    showHeaderMenu() {
      return !(this.hideHeaderItems || this.foreignDomainFromCathay || this.foreignDomainFromMichelin)
    },
    showFooter() {
      return !this.foreignDomainFromMichelin && !this.hideFooter
    },
    ...mapGetters({
      foreignDomainFromMichelin: 'foreignDomainFromMichelin',
      foreignDomainFromCathay: 'foreignDomainFromCathay',
      fromTngMiniProgram: 'fromTngMiniProgram',
      isBookingEngine: 'isBookingEngine',
    }),
    homeLink() {
      if (this.isBookingEngine) {
        return { path: this.localePath('/'), query: this.$route.query }
      }
      if (this.isFunBookV2) {
        return this.localePath({
          name: 'funbook-branch_id-branch',
          params: { branch_id: this.branchId },
          query: this.$route.query,
        })
      }
      return this.localePath('/')
    },
  },
  mounted() {
    this.getUserInfo()
    if (this.foreignDomainFromMichelin) {
      window.sessionStorage.setItem('3rd_Source', 'Michelin')
    }

    if (this.foreignDomainFromCathay) {
      window.sessionStorage.setItem('3rd_Source', 'Cathay')
    }
  },
  methods: {
    getUserInfo() {
      this.$store
        .dispatch('authentication/getUserInfo')
        .then(res => {
          this.loginLoading = false
        })
        .catch(err => {
          console.error('getUserInfo', err)
        })
    },
  },
})
</script>

<style lang="scss" src="@/assets/sass/pages/index.scss"></style>
<style scoped lang="scss" src="@/assets/sass/components/core/header.scss"></style>
